'use client';

import { useRef, useState } from 'react';
import {
  motion,
  useInView,
  useMotionValue,
  useMotionTemplate,
  useReducedMotion,
  useSpring,
  useTransform,
} from 'framer-motion';
import { clsx } from 'clsx';
import { toImageItems } from '@/lib/media';
import { SmartImage } from './Skeleton';
import Lightbox from './Lightbox';

// Staggered 2-column bento. Each cell spans two row tracks; the layout fits up to
// five images (the painting/walls set). Extra images are reachable in the lightbox.
const areaClasses = [
  'col-start-2 col-end-3 row-start-1 row-end-3',
  'col-start-1 col-end-2 row-start-2 row-end-4',
  'col-start-1 col-end-2 row-start-4 row-end-6',
  'col-start-2 col-end-3 row-start-3 row-end-5',
  'col-start-2 col-end-3 row-start-5 row-end-7',
];

const MAX_TILT = 11; // degrees the card rotates toward the cursor at the edges
const SPRING = { stiffness: 220, damping: 18, mass: 0.4 };

interface TiltCardProps {
  src: string;
  index: number;
  label: string;
  inView: boolean;
  areaClass: string;
  onOpen: () => void;
}

// One bento tile that tilts in 3D toward the cursor, lifts slightly, and carries a
// soft glare that tracks the pointer — falls back to a flat zoom for reduced motion.
function TiltCard({ src, index, label, inView, areaClass, onOpen }: TiltCardProps) {
  const reduceMotion = useReducedMotion();
  const ref = useRef<HTMLButtonElement>(null);

  // Pointer position over the card, normalised to -0.5..0.5 on each axis.
  const px = useMotionValue(0);
  const py = useMotionValue(0);
  const sx = useSpring(px, SPRING);
  const sy = useSpring(py, SPRING);

  const rotateX = useTransform(sy, [-0.5, 0.5], [MAX_TILT, -MAX_TILT]);
  const rotateY = useTransform(sx, [-0.5, 0.5], [-MAX_TILT, MAX_TILT]);

  // Glare highlight follows the cursor across the tile.
  const glareX = useTransform(sx, [-0.5, 0.5], ['0%', '100%']);
  const glareY = useTransform(sy, [-0.5, 0.5], ['0%', '100%']);
  const glare = useMotionTemplate`radial-gradient(circle at ${glareX} ${glareY}, rgba(255,255,255,0.22), transparent 55%)`;

  function handleMove(e: React.MouseEvent<HTMLButtonElement>) {
    if (reduceMotion) return;
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    px.set((e.clientX - rect.left) / rect.width - 0.5);
    py.set((e.clientY - rect.top) / rect.height - 0.5);
  }

  function reset() {
    px.set(0);
    py.set(0);
  }

  return (
    <motion.button
      ref={ref}
      type="button"
      // Roller-sweep entrance: each tile wipes in top-to-bottom like a paint
      // stroke (this gallery backs the Painting section). Reduced motion: fade.
      initial={reduceMotion ? { opacity: 0 } : { clipPath: 'inset(0 0 100% 0)' }}
      animate={
        inView ? (reduceMotion ? { opacity: 1 } : { clipPath: 'inset(0 0 0% 0)' }) : undefined
      }
      transition={{ duration: 0.55, delay: index * 0.12, ease: [0.23, 1, 0.32, 1] }}
      onClick={onOpen}
      onMouseMove={handleMove}
      onMouseLeave={reset}
      whileHover={reduceMotion ? undefined : { scale: 1.03 }}
      style={
        reduceMotion
          ? undefined
          : { rotateX, rotateY, transformPerspective: 900, transformStyle: 'preserve-3d' }
      }
      aria-label={`${label} ${index + 1} — open`}
      className={clsx(
        'forest-img-shadow group relative overflow-hidden rounded-sm border border-surface-bright/20 transform-gpu transition-colors duration-300 hover:z-10 hover:border-surface-bright/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        areaClass
      )}
    >
      <SmartImage
        src={src}
        alt={`${label} ${index + 1}`}
        fill
        sizes="(max-width: 1024px) 50vw, 28vw"
        className="object-cover transition-transform duration-500 ease-out group-hover:scale-110"
      />
      {/* Pointer-tracking glare — only paints in once you hover. */}
      {!reduceMotion && (
        <motion.span
          aria-hidden
          style={{ backgroundImage: glare }}
          className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
        />
      )}
    </motion.button>
  );
}

interface PhotoGalleryProps {
  images: string[];
  label: string;
}

export default function PhotoGallery({ images, label }: PhotoGalleryProps) {
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const gridRef = useRef<HTMLDivElement>(null);
  const inView = useInView(gridRef, { once: true, margin: '-10% 0px' });

  const cells = images.slice(0, areaClasses.length);

  function openAt(i: number) {
    setIndex(i);
    setOpen(true);
  }

  return (
    <>
      <div
        ref={gridRef}
        className="grid grid-cols-2 grid-rows-[50px_150px_50px_150px_50px_150px_50px] gap-6 [perspective:1200px]"
      >
        {cells.map((src, i) => (
          <TiltCard
            key={src}
            src={src}
            index={i}
            label={label}
            inView={inView}
            areaClass={areaClasses[i]}
            onOpen={() => openAt(i)}
          />
        ))}
      </div>

      <Lightbox
        items={toImageItems(images)}
        index={index}
        open={open}
        label={label}
        onClose={() => setOpen(false)}
        onIndexChange={setIndex}
      />
    </>
  );
}
